import {
  Body,
  Controller,
  Delete,
  Get,
  HttpCode,
  Param,
  Patch,
  Post,
  Query,
  Req,
  UseGuards,
} from '@nestjs/common';
import {
  ApiBearerAuth,
  ApiBody,
  ApiOperation,
  ApiParam,
  ApiQuery,
  ApiResponse,
  ApiSecurity,
  ApiTags,
} from '@nestjs/swagger';
import { CreateReviewRequest, ReviewEligibilityResponse, ReviewListResponse, ReviewsMineBatchRequest, ReviewsMineBatchResponse, ReviewSummary, ReviewTypeCode, UpdateReviewRequest } from '@cart/shared-types';
import { GuardedRequest, AuthGuard } from '../auth/auth.guard';
import { ReviewsService } from './reviews.service';

@ApiTags('Reviews')
@ApiSecurity('store-id')
@Controller('reviews')
export class ReviewsController {
  constructor(private readonly reviewsService: ReviewsService) {}

  @Get()
  @ApiOperation({ summary: 'List published reviews for a review type, optionally scoped to one target' })
  @ApiQuery({ name: 'type', enum: ['product', 'delivery_partner', 'order', 'general'] })
  @ApiQuery({ name: 'targetId', required: false, description: 'productId / orderId — omit for general reviews' })
  @ApiResponse({ status: 200, description: 'Returns reviews with average rating and total count' })
  list(
    @Query('type') type: ReviewTypeCode,
    @Query('targetId') targetId?: string
  ): Promise<ReviewListResponse> {
    return this.reviewsService.list(type, targetId);
  }

  @Get('mine')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Get the current user\'s own review for a review type/target, if one exists' })
  @ApiQuery({ name: 'type', enum: ['product', 'delivery_partner', 'order', 'general'] })
  @ApiQuery({ name: 'targetId', required: false })
  @ApiResponse({ status: 200, description: 'Returns the review, or null if none exists yet' })
  async mine(
    @Req() request: GuardedRequest,
    @Query('type') type: ReviewTypeCode,
    @Query('targetId') targetId?: string
  ): Promise<ReviewSummary | null> {
    const review = await this.reviewsService.mine(request.user.id, type, targetId);
    return review ?? null;
  }

  @Post('mine/batch')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Get the current user\'s own reviews for many targets of one review type at once' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['reviewType', 'targetIds'],
      properties: {
        reviewType: { type: 'string', enum: ['product', 'delivery_partner', 'order', 'general'] },
        targetIds: { type: 'array', items: { type: 'string' } },
      },
    },
  })
  @ApiResponse({ status: 200, description: 'Map of targetId -> review, for targets that have one' })
  mineBatch(
    @Req() request: GuardedRequest,
    @Body() body: ReviewsMineBatchRequest
  ): Promise<ReviewsMineBatchResponse> {
    return this.reviewsService.mineBatch(request.user.id, body.reviewType, body.targetIds ?? []);
  }

  @Get('eligibility')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Whether the current user may write this review yet (purchased + delivered)' })
  @ApiQuery({ name: 'type', enum: ['product', 'delivery_partner', 'order', 'general'] })
  @ApiQuery({ name: 'targetId', required: false })
  eligibility(
    @Req() request: GuardedRequest,
    @Query('type') type: ReviewTypeCode,
    @Query('targetId') targetId?: string
  ): Promise<ReviewEligibilityResponse> {
    return this.reviewsService.eligibility(request.user.id, type, targetId);
  }

  @Post()
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Create a review (product, delivery partner, order, or general)' })
  @ApiBody({
    schema: {
      type: 'object',
      required: ['reviewType', 'rating'],
      properties: {
        reviewType: { type: 'string', enum: ['product', 'delivery_partner', 'order', 'general'] },
        targetId: { type: 'string', description: 'productId or orderId — omit for general reviews' },
        rating: { type: 'number', example: 5 },
        title: { type: 'string' },
        comment: { type: 'string' },
        attributes: { type: 'object', example: { packaging: 5, wouldRecommend: true } },
      },
    },
  })
  @ApiResponse({ status: 201, description: 'Review created' })
  @ApiResponse({ status: 409, description: 'A review already exists for this user/type/target' })
  create(
    @Req() request: GuardedRequest,
    @Body() body: CreateReviewRequest
  ): Promise<ReviewSummary> {
    return this.reviewsService.create(request.user.id, body);
  }

  @Patch(':id')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @ApiOperation({ summary: 'Update the current user\'s own review' })
  @ApiParam({ name: 'id', description: 'Review id' })
  @ApiResponse({ status: 200, description: 'Review updated' })
  @ApiResponse({ status: 404, description: 'Review not found' })
  update(
    @Req() request: GuardedRequest,
    @Param('id') id: string,
    @Body() body: UpdateReviewRequest
  ): Promise<ReviewSummary> {
    return this.reviewsService.update(request.user.id, id, body);
  }

  @Delete(':id')
  @UseGuards(AuthGuard)
  @ApiBearerAuth()
  @HttpCode(204)
  @ApiOperation({ summary: 'Delete the current user\'s own review' })
  @ApiParam({ name: 'id', description: 'Review id' })
  @ApiResponse({ status: 204, description: 'Review deleted' })
  @ApiResponse({ status: 404, description: 'Review not found' })
  remove(@Req() request: GuardedRequest, @Param('id') id: string): Promise<void> {
    return this.reviewsService.delete(request.user.id, id);
  }
}
